import Cookies from 'cookies-js';

/**
 * Reads one SSE frame ("event: x\ndata: {...}") into { event, data }.
 * Ignores comment lines (`:`) and the fields the API does not send (id, retry).
 */
export const parseFrame = (frame) => {
  let event = 'message';
  const data = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trim());
  }
  if (!data.length) return null;
  try {
    return { event, data: JSON.parse(data.join('\n')) };
  } catch {
    return null;
  }
};

/**
 * Opens an admin-authenticated SSE stream and calls `onEvent` per frame until
 * the server ends it or `signal` aborts. Resolves when the stream ends;
 * rejects if it never opened.
 *
 * `fetch` rather than `EventSource` because the dashboard authenticates with
 * an `authorization` header and EventSource cannot set one — the alternative
 * is putting the admin JWT in the query string, where it lands in access logs.
 */
export const readSseStream = async ({ url, signal, onOpen, onEvent }) => {
  const response = await fetch(url, {
    headers: { authorization: Cookies.get('user'), accept: 'text/event-stream' },
    signal,
  });
  if (!response.ok || !response.body) throw new Error(`stream ${response.status}`);
  if (onOpen) onOpen();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    // Frames are separated by a blank line; the trailing chunk is a partial
    // frame and has to stay in the buffer until its blank line arrives, or an
    // event gets split down the middle and dropped.
    const frames = buffer.split('\n\n');
    buffer = frames.pop();
    for (const frame of frames) {
      const parsed = parseFrame(frame);
      if (parsed) onEvent(parsed);
    }
  }
};
